import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import { useAuth } from '../contexts/AuthContext';
import { type AppRole, appRoleService } from '../services/appRoleService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateTimeDdMmYyyy } from '../utils/dateUtils';
import { AppRoleDrawer } from './AppRoleDrawer';

type PanelMode = 'empty' | 'view' | 'edit' | 'add';

export const AppRoleManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['app-roles'],
    queryFn: () => appRoleService.getAll(),
    refetchOnMount: 'always',
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedRow, setSelectedRow] = useState<AppRole | null>(null);
  const [editingRow, setEditingRow] = useState<AppRole | null>(null);

  const currentUserName = (user?.userId ?? '').trim().toLowerCase();

  const handleAdd = () => {
    setEditingRow(null);
    setPanelMode('add');
  };

  const handleDelete = async (row: AppRole) => {
    const isSelf = row.user_name.trim().toLowerCase() === currentUserName;
    const message = isSelf
      ? `Remove your own access (${row.user_name})? You will be signed out and unable to sign back in.`
      : `Remove app access for "${row.user_name}"?`;
    if (!window.confirm(message)) return;
    try {
      await appRoleService.delete(row.id);
      queryClient.invalidateQueries({ queryKey: ['app-roles'] });
      setSelectedRow(null);
      setPanelMode('empty');
    } catch (err) {
      toast.error(
        `Failed to remove access: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const handleRowClicked = useCallback((event: RowClickedEvent<AppRole>) => {
    if (!event.data) return;
    setSelectedRow(event.data);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingRow(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingRow(null);
      return;
    }
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedRow(null);
  }, []);

  const columnDefs: ColDef<AppRole>[] = useMemo(
    () => [
      {
        field: 'user_name',
        headerName: 'User Name',
        flex: 1,
        minWidth: 160,
        cellRenderer: (params: { value?: string }) => {
          const name = params.value || '-';
          const isSelf = (params.value ?? '').trim().toLowerCase() === currentUserName;
          return (
            <span>
              {name}
              {isSelf ? <span className="ml-2 text-xs text-text-secondary">(you)</span> : null}
            </span>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 120,
        cellRenderer: (params: { value?: string }) => {
          const role = params.value || 'user';
          const roleClass =
            role === 'admin'
              ? 'bg-primary-500/20 text-primary-600'
              : 'bg-muted text-text-secondary';
          return (
            <span className={`rounded px-2 py-1 text-xs font-semibold capitalize ${roleClass}`}>
              {role}
            </span>
          );
        },
      },
      {
        field: 'updated_at',
        headerName: 'Last Changed',
        width: 170,
        valueFormatter: (p) => formatDateTimeDdMmYyyy(p.value),
      },
      {
        field: 'updated_by',
        headerName: 'Changed By',
        width: 150,
        valueFormatter: (p) => p.value || '-',
      },
    ],
    [currentUserName]
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <div className="p-2">
      <style>{agGridThemeStyles}</style>
      <SettingsListDetailLayout
        detailOpen={detailOpen}
        detailVariant="default"
        detail={
          panelMode === 'add' ? (
            <AppRoleDrawer
              isOpen={true}
              onClose={handleAddClose}
              onSave={() => {}}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['app-roles'] });
                setPanelMode('empty');
                setSelectedRow(null);
                setEditingRow(null);
              }}
              row={null}
              isEdit={false}
            />
          ) : panelMode === 'edit' && editingRow ? (
            <AppRoleDrawer
              isOpen={true}
              onClose={handleEditClose}
              onSave={(row) => setSelectedRow(row)}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['app-roles'] });
                setPanelMode('view');
                setEditingRow(null);
              }}
              row={editingRow}
              isEdit
            />
          ) : panelMode === 'view' && selectedRow ? (
            <AppRoleDrawer
              key={selectedRow.id}
              isOpen={true}
              readOnly
              row={selectedRow}
              onClose={handleViewClose}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingRow(selectedRow);
                setPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDelete(selectedRow)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-4 flex shrink-0 items-center justify-end">
              <Button type="button" onClick={handleAdd}>
                <PlusIcon data-icon="inline-start" />
                Add User
              </Button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-4 p-8 text-text-secondary">
                <Spinner className="size-10" />
                <p>Loading users...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center p-8 text-danger-600">
                <p>Error loading data: {error.message}</p>
                <Button type="button" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <p className="p-8 text-center text-text-secondary">
                No app users found. Add your first user to get started.
              </p>
            ) : (
              <div
                className="ag-theme-material min-h-0 w-full flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  theme={agGridTheme}
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  domLayout="normal"
                  rowNumbers
                  getRowId={(params) => String(params.data.id)}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
